import React, { useEffect, useState } from "react";
import { Download, Search, MessageCircle, ExternalLink } from "lucide-react";
import { adminApi, downloadCsv } from "@/admin/adminApi";
import { formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const STATUSES = ["pending", "awaiting_payment", "paid", "preparing", "shipped", "delivered", "cancelled"];
const STATUS_LABELS = {
  pending: "Order Request Received",
  awaiting_payment: "Awaiting Payment",
  paid: "Paid / Confirmed",
  preparing: "Preparing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  confirmed: "Paid / Confirmed",
  packed: "Preparing",
};
const statusLabel = (s) => STATUS_LABELS[s] || s;

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setOrders(null);
    try { const { data } = await adminApi.get("/orders"); setOrders(data); }
    catch { setOrders([]); toast.error("Could not load orders"); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/orders/${id}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Status updated");
    } catch { toast.error("Could not update status"); }
  };

  const exportCsv = () => downloadCsv("/orders-export", "wonder-parcel-orders.csv").catch(() => toast.error("Export failed"));

  const filtered = (orders || []).filter((o) => {
    if (!query) return true;
    const ql = query.toLowerCase();
    const c = o.customer || {};
    return o.id.toLowerCase().includes(ql) || (c.name || "").toLowerCase().includes(ql) || (c.phone || "").includes(ql) || (o.status || "").includes(ql);
  });

  return (
    <div data-testid="admin-orders">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl">Orders</h1><p className="text-sm text-wp-ink-muted">{orders ? `${filtered.length} order(s)` : "Loading…"}</p></div>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-wp-ink-muted" /><Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search orders…" className="w-48 rounded-wp-md pl-8" data-testid="orders-search" /></div>
          <Button onClick={exportCsv} variant="outline" className="rounded-xl" data-testid="orders-export-button"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft">
        {orders === null ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-wp-ink-muted">No orders found.</div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Links</TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell><div className="text-sm font-medium">#{o.id.slice(0, 8)}</div><div className="text-xs text-wp-ink-muted">{new Date(o.created_at).toLocaleDateString()}</div></TableCell>
                  <TableCell><div className="text-sm">{o.customer?.name || "Guest"}</div><div className="text-xs text-wp-ink-muted">{o.customer?.phone}</div></TableCell>
                  <TableCell className="text-sm">{(o.items || []).length}</TableCell>
                  <TableCell className="price-num text-sm font-semibold">{formatINR(o.subtotal)}</TableCell>
                  <TableCell>
                    <Select value={STATUS_LABELS[o.status] ? o.status : "pending"} onValueChange={(v) => setStatus(o.id, v)}>
                      <SelectTrigger className="h-8 w-44 rounded-lg text-xs" data-testid={`order-status-${o.id}`}><SelectValue>{statusLabel(o.status)}</SelectValue></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{statusLabel(s)}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {o.whatsapp_url && <a href={o.whatsapp_url} target="_blank" rel="noreferrer" className="grid h-8 w-8 place-items-center rounded-lg text-wp-success hover:bg-wp-success/10" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                      {o.gift_url && <a href={o.gift_url} target="_blank" rel="noreferrer" className="grid h-8 w-8 place-items-center rounded-lg text-wp-rose-strong hover:bg-wp-blush-100" title="Gift page"><ExternalLink className="h-4 w-4" /></a>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
